import type { TextStyle } from 'react-native'

import {
  FONT_STYLE_CSS_WEIGHT,
  type FunkitFontStyleName,
  type FunkitTokens,
  fontFamilyForWeight,
} from './tokens'

// A resolved text cut: the native family (weight baked into the name, see
// `fontFamilyForWeight`) plus the numeric weight kept for the Swapped WebView
// (CSS) theme and the system-font fallback.
function cut(family: string, styleName: FunkitFontStyleName) {
  return {
    fontFamily: fontFamilyForWeight(family, styleName),
    fontWeight: FONT_STYLE_CSS_WEIGHT[styleName],
  }
}

/**
 * Projects the nested Fun Variables {@link FunkitTokens} onto the object
 * `@shopify/restyle`'s `createTheme` consumes — kept as a pure function (no
 * Restyle import) so tests can build the theme without pulling Restyle's
 * react-native Flow source into the node env.
 *
 * The design deliberately DOESN'T use Restyle's `colors` prop system: `colors`
 * is empty and components read colors straight from the Figma-shaped groups
 * (`theme.background.button.primary.default`, `theme.content.elements.primary`,
 * …) via `useTheme()`, so the theme stays a faithful mirror of Figma. Only the
 * non-color numeric buckets Restyle genuinely needs (`spacing`, `borderRadii`,
 * `borderWidths`, `sizes`, `breakpoints`) are projected onto its reserved keys.
 */
export function buildFunkitTheme(tokens: FunkitTokens) {
  const { font } = tokens
  const base = cut(font.body.family, font.body.weight.base)
  const medium = cut(font.body.family, font.body.weight.medium)
  const semibold = cut(font.body.family, font.body.weight.semibold)
  const header = cut(font.header.family, font.header.weight)
  const headerAlign =
    font.header.alignment.toLowerCase() as TextStyle['textAlign']

  return {
    // Restyle's BaseTheme requires `colors`; intentionally empty — see above.
    colors: {},

    // ── Figma variable groups, verbatim ───────────────────────────────
    background: tokens.background,
    content: tokens.content,
    border: tokens.border,
    brand: tokens.brand,
    opacity: tokens.opacity,
    font: tokens.font,

    // Local to the KYC design file, not yet Fun Variables — see `tokens.ts`.
    form: tokens.form,

    // ── Non-color numeric buckets projected onto Restyle's reserved keys ─
    spacing: {
      none: 0,
      elementsHorizontal: tokens.spacing.padding.elements.horizontal,
      modalHorizontal: tokens.spacing.padding.modal.horizontal,
    },
    borderRadii: {
      modal: tokens.border.radius.modal,
      element: tokens.border.radius.element,
      elementInner: tokens.border.radius.elementInner,
      button: tokens.border.radius.button,
      // Structural (no Figma token): fully-circular avatars / icon wells.
      rounded: 999,
    },
    borderWidths: {
      modal: tokens.border.modal.weight,
      elementsDefault: tokens.border.elements.default.weight,
      elementsSelected: tokens.border.elements.selected.weight,
      button: tokens.border.button.weight,
    },
    sizes: {
      topBar: tokens.spacing.heights.topBar,
      buttonHeight: tokens.spacing.heights.button,
      buttonXs: tokens.button.size.xs,
      buttonSm: tokens.button.size.sm,
      buttonMd: tokens.button.size.md,
      buttonLg: tokens.button.size.lg,
      iconXs: tokens.button.icon.size.xs,
      iconSm: tokens.button.icon.size.sm,
      iconMd: tokens.button.icon.size.md,
      iconLg: tokens.button.icon.size.lg,
    },
    breakpoints: {
      phone: 0,
      tablet: 768,
    },

    // Text variants carry no `color` (Restyle colors is unused); the `Text`
    // component defaults the color from `content/elements/*` and callers
    // override per-node via `style`.
    textVariants: {
      defaults: {
        ...base,
        fontSize: font.body.size.base,
        lineHeight: font.body.leading.base,
      },
      body: {
        ...base,
        fontSize: font.body.size.base,
        lineHeight: font.body.leading.base,
      },
      bodyMedium: {
        ...medium,
        fontSize: font.body.size.base,
        lineHeight: font.body.leading.base,
      },
      detail: {
        ...base,
        fontSize: font.body.size.detail,
        lineHeight: font.body.leading.detail,
      },
      headerTitle: {
        ...header,
        fontSize: font.header.size.title,
        textAlign: headerAlign,
      },
      headerSubtitle: {
        ...header,
        fontSize: font.header.size.subtitle,
        textAlign: headerAlign,
      },
      // A drawer heading sets its own line box and optical tracking, and takes
      // the paragraph alignment rather than the top bar's centred one.
      drawerTitle: {
        ...header,
        fontSize: font.header.size.drawerTitle,
        lineHeight: Math.round(font.header.size.drawerTitle * 1.3),
        letterSpacing: font.header.size.drawerTitle * -0.03,
      },
      amountInput: {
        ...medium,
        fontSize: font.header.size.amountInput,
      },
      amountSummary: {
        ...medium,
        fontSize: font.header.size.amountSummary,
      },
    },
    // Per-weight `{ fontFamily, fontWeight }` the `Text` `weight` prop applies on
    // top of any variant (both are required for custom fonts — see
    // `fontFamilyForWeight`).
    textWeights: {
      base,
      medium,
      semibold,
    },
  }
}
